import { Injectable, Signal, computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { SessionService } from './session.service';
import { StorageService } from './storage.service';
import { StorageKey, userPrefKey } from './storage-keys';
import { userPreference } from './user-preference';

const NONE: readonly string[] = Object.freeze([]);

/**
 * The navbar project selector's two preferences: the full checked-project set (0, 1, or N ids),
 * not just the single "active" project `ProjectService` tracks, and which projects are pinned to
 * the top of the list. `ProjectSelectorComponent` writes both; pages that need to react to
 * multi-selection (e.g. the Sites page's per-project tables) read here instead of duplicating the
 * selector's own bookkeeping.
 *
 * Both are scoped by user, so neither can be read before the session has arrived — hence the
 * empty set the whole boot window sees, and `whenReady()` for the selector, which has to know
 * the difference between "nothing chosen" and "not asked yet" before it defaults a selection.
 */
@Injectable({
  providedIn: 'root',
})
export class ProjectSelectionService {
  private readonly storage = inject(StorageService);
  private readonly session = inject(SessionService);

  private readonly selected = this.preference(userPrefKey.selectedProjects);
  private readonly pinned = this.preference(userPrefKey.pinnedProjects);

  readonly selectedProjectIds: Signal<ReadonlySet<string>> = computed(() => new Set(this.selected.value()));
  readonly pinnedProjectIds: Signal<ReadonlySet<string>> = computed(() => new Set(this.pinned.value()));

  readonly selectedProjectIds$ = toObservable(this.selectedProjectIds);

  setSelectedProjectIds(ids: ReadonlySet<string>): void {
    this.selected.set([...ids]);
  }

  setPinnedProjectIds(ids: ReadonlySet<string>): void {
    this.pinned.set([...ids]);
  }

  /**
   * Resolves once the session has settled, with whether it produced an account.
   *
   * `false` is the answer the selector cannot skip: without an account the persisted sets read as
   * empty, and an empty set is indistinguishable from "nothing picked" — restore on that and the
   * selector writes a default over a choice it simply could not see.
   */
  async whenReady(): Promise<boolean> {
    await this.session.load();
    return this.session.userId() !== undefined;
  }

  private preference(key: (userId: string) => StorageKey) {
    return userPreference<readonly string[]>({
      userId: this.session.userId,
      read: (userId) => this.storage.getStringArray(key(userId)),
      write: (userId, ids) => this.storage.setJson(key(userId), ids),
      empty: NONE,
    });
  }
}
